import {AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild, ViewRef} from "@angular/core";
import {takeUntilDestroyed, outputToObservable} from "@angular/core/rxjs-interop";
import {LiveAnnouncer} from "@angular/cdk/a11y";
import {NgSelectComponent} from "@ng-select/ng-select";
import {Subject} from "rxjs";
import {of} from "rxjs";
import {debounceTime, switchMap, map} from "rxjs/operators";
import {TranslateService} from '@ngx-translate/core';


export class SelectItem {

  label: string;
  value: string;
  styleClass?:string;
  object: any = null;

}

@Component({
  standalone: false,
  selector: "wfm-select",
  templateUrl: "./wfm-select.component.html",
  styleUrls: ["./wfm-select.component.scss"]
})
export class WfmSelectComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() required = true;
  @Input() label = null;
  @Input() placeHolder = null;
  @Input() inlineStyleObject = {};
  @Input() wfmAutoComplete = "on";
  @Input() selectId = null;
  @Input() selectName = null;
  @Input() items: Array<SelectItem>;
  @Input() selectedItem: SelectItem;
  @Input() disabled: boolean;
  @Input() showLoading: boolean;
  @Output() wfmSelectEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectOnBlur: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectOnChange: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild("wfmSelect") ngSelect: NgSelectComponent;

  noItemsMessage: string = '';
  private searchSubject = new Subject<{ term: string; items: any[] }>();

  /**
   * Cached text of an external `<label for>` / `aria-labelledby` association,
   * resolved imperatively after view init (see `refreshExternalLabelText`,
   * called from `applyAriaControls`). Held in a field rather than read from the
   * DOM inside `comboAriaLabel` so the template-bound accessible name stays
   * stable within a change-detection cycle. Reading the external label's
   * `textContent` directly in the getter throws NG0100
   * (ExpressionChangedAfterItHasBeenChecked) when that text resolves
   * asynchronously — e.g. an i18n `| translate` label that is the raw key
   * ('login.language') on one CD pass and the translated value on the next.
   */
  private externalLabelText = '';

  /**
   * Accessible name for the ng-select dropdown listbox. Prefers the field
   * label, then the placeholder, so screen readers announce the field name
   * instead of ng-select's generic "Options List".
   */
  get dropdownAriaLabel(): string {
    return (this.label ?? '').trim() || (this.placeHolder ?? '').trim() || '';
  }

  /**
   * Accessible name for the combobox input, bound to ng-select's own
   * `ariaLabel` input (`[ariaLabel]` in the template) so the selected value is
   * surfaced through ng-select's `[attr.aria-label]="ariaLabel()"` binding.
   * This must be driven through the framework binding rather than set
   * imperatively: ng-select re-applies its own (empty) `ariaLabel` on every
   * change-detection cycle, which silently wipes any `aria-label` set via
   * `Renderer2.setAttribute` before the screen reader reads it. Returns
   * "<label>, <selected value>" (e.g. "Forecast Source, WFM Forecast") so JAWS
   * announces the current selection (WCAG 4.1.2 Name, Role, Value), or `null`
   * when nothing is selected so the control falls back to its `<label for>`
   * association.
   */
  get comboAriaLabel(): string | null {
    const selectedText = this.getSelectedLabel();
    if (!selectedText) {
      return null;
    }
    // Resolve the field label from stable component inputs first, then fall
    // back to the imperatively-cached external-label text. This getter is
    // bound to `[ariaLabel]`, so it must NOT read the DOM (an external
    // `<label>.textContent`) here: that text can resolve asynchronously (e.g.
    // an i18n `| translate` label), producing a different value between
    // Angular's detect-changes and check-no-changes passes and throwing
    // NG0100 ExpressionChangedAfterItHasBeenChecked. See `externalLabelText`.
    const labelText = (this.label ?? '').trim()
      || (this.placeHolder ?? '').trim()
      || this.externalLabelText;
    return labelText ? `${labelText}, ${selectedText}` : selectedText;
  }

  constructor(private cd: ChangeDetectorRef, private translateSrv: TranslateService,
              private elementRef: ElementRef, private renderer: Renderer2,
              private destroyRef: DestroyRef, private liveAnnouncer: LiveAnnouncer) {
    this.items = new Array<SelectItem>();
  }

  ngOnInit() {
    this.searchSubject.pipe(
      debounceTime(500),
      switchMap(event => {
        if (event.term && event.items.length === 0) {
          return this.translateSrv.get('dropdown.no.items').pipe(
            map(translated => ({ event, translated }))
          );
        }
        return of({ event, translated: '' });
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ translated }) => {
      this.announceNoResults(translated);
    });
  }

  onSearch(event: { term: string; items: any[] }) {
    this.searchSubject.next(event);
  }

  /**
   * Updates the single custom aria-live region with the no-results status.
   * Clears first so an identical consecutive message is still re-announced,
   * and flushes change detection because the host is OnPush.
   */
  private announceNoResults(message: string) {
    this.noItemsMessage = '';
    this.flushLiveRegion();
    if (message) {
      this.noItemsMessage = message;
      this.flushLiveRegion();
    }
  }

  /** OnPush-safe synchronous DOM flush for the live region. */
  private flushLiveRegion() {
    if (!(this.cd as ViewRef).destroyed) {
      this.cd.detectChanges();
    }
  }

  /**
   * Neutralizes ng-select's own always-present aria-live region. On this
   * version `notFoundText` is a signal input but the built-in region binds it
   * without invoking it, so it announces garbage like "[Input Signal: ...]"
   * and duplicates our clean status message. We expose a single custom region
   * instead, so silence the built-in one.
   */
  private suppressBuiltInLiveRegion() {
    this.elementRef.nativeElement
      .querySelectorAll('.ng-visually-hidden[role="status"]')
      .forEach((el: Element) => {
        this.renderer.setAttribute(el, 'aria-hidden', 'true');
        this.renderer.setAttribute(el, 'aria-live', 'off');
      });
  }

  ngAfterViewInit() {
    const panelId = `${this.selectId}-listbox`;

    const applyAriaControls = () => {
      const input: HTMLElement = this.elementRef.nativeElement.querySelector('input[role="combobox"]');
      if (input) {
        this.renderer.setAttribute(input, 'aria-controls', panelId);
        if (this.required) {
          this.renderer.setAttribute(input, 'aria-required', 'true');
        } else {
          this.renderer.removeAttribute(input, 'aria-required');
        }
      }
      this.elementRef.nativeElement
        .querySelectorAll('.ng-arrow-wrapper, .ng-arrow-wrapper *')
        .forEach((el: Element) => this.renderer.setAttribute(el, 'aria-hidden', 'true'));
      this.suppressBuiltInLiveRegion();
      this.refreshExternalLabelText();
    };

    setTimeout(applyAriaControls, 0);

    outputToObservable(this.ngSelect.openEvent).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      setTimeout(() => {
        const scrollHost: HTMLElement = this.elementRef.nativeElement.querySelector('.ng-dropdown-panel-items.scroll-host')
          || document.querySelector('.ng-dropdown-panel-items.scroll-host');
        if (scrollHost && !scrollHost.getAttribute('tabindex')) {
          this.renderer.setAttribute(scrollHost, 'tabindex', '0');
        }
        const panel: HTMLElement = this.elementRef.nativeElement.querySelector('.ng-dropdown-panel')
          || document.querySelector('.ng-dropdown-panel');
        if (panel) {
          panel.querySelectorAll('.ng-option.ng-option-disabled').forEach((el: Element) => {
            this.renderer.setAttribute(el, 'aria-hidden', 'true');
          });
        }
        applyAriaControls();
      }, 0);
    });

    outputToObservable(this.ngSelect.closeEvent).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.searchSubject.next({ term: '', items: [{}] });
      setTimeout(applyAriaControls, 0);
    });

    outputToObservable(this.ngSelect.focusEvent).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => applyAriaControls());
    outputToObservable(this.ngSelect.blurEvent).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => applyAriaControls());
  }

  updateValue() {
    var selectedValue = this.ngSelect.selectedItems[0].value;
    // Re-resolve the cached external-label text on selection so the accessible
    // name reflects the new value immediately (the setTimeout in ngAfterViewInit
    // only covers init / open / close / focus / blur).
    this.refreshExternalLabelText();
    this.wfmSelectEvent.emit(selectedValue);
    this.announceSelection();
  }

  /**
   * Announces the newly selected option through the body-level CDK
   * LiveAnnouncer. ng-select keeps DOM focus on the combobox input and uses
   * `aria-activedescendant` for option navigation, so choosing an option does
   * not move focus and screen readers never re-read the combobox. Without an
   * explicit live announcement JAWS stays silent on selection and the value is
   * only surfaced on the next focus via the combobox `aria-label` (WCAG 4.1.3
   * Status Messages). Announces "<label>, <selected value>" to match the
   * combobox accessible name, e.g. "Forecast Source, WFM Forecast".
   */
  private announceSelection() {
    const message = this.comboAriaLabel;
    if (message) {
      this.liveAnnouncer.clear();
      this.liveAnnouncer.announce(message, 'polite');
    }
  }

  ngOnDestroy() {
    this.liveAnnouncer.clear();
  }

  /**
   * Resolves the control's visible label text for the accessible name. Because
   * `aria-label` overrides any `<label for>` / `aria-labelledby` association,
   * this keeps the control label in the accessible name even when the component
   * `label`/`placeHolder` inputs are not set and the control is labelled
   * externally (e.g. a consuming template supplies its own `<label for>`).
   */
  private getAssociatedLabelText(comboInput: HTMLElement): string {
    const explicit = this.label ?? this.placeHolder ?? '';
    if (explicit) {
      return explicit;
    }
    const inputId = comboInput.getAttribute('id');
    if (inputId) {
      const labelEl = this.elementRef.nativeElement.querySelector(`label[for="${inputId}"]`)
        ?? document.querySelector(`label[for="${inputId}"]`);
      const labelText = labelEl?.textContent?.trim();
      if (labelText) {
        return labelText;
      }
    }
    const labelledBy = comboInput.getAttribute('aria-labelledby');
    if (labelledBy) {
      const text = labelledBy.split(/\s+/)
        .map(id => document.getElementById(id)?.textContent?.trim())
        .filter(Boolean)
        .join(' ');
      if (text) {
        return text;
      }
    }
    return '';
  }

  /**
   * Resolve and cache the text of an external `<label for>` / `aria-labelledby`
   * association into `externalLabelText`. Runs imperatively (post-view, inside
   * the `applyAriaControls` setTimeout) so the DOM read happens AFTER any async
   * i18n label text has resolved, and so `comboAriaLabel` can build the
   * accessible name from a stable field instead of reading the DOM during
   * change detection (avoids NG0100).
   */
  private refreshExternalLabelText() {
    const comboInput: HTMLElement | null =
      this.elementRef?.nativeElement?.querySelector('input[role="combobox"]');
    const resolved = comboInput ? this.getAssociatedLabelText(comboInput) : '';
    if (resolved !== this.externalLabelText) {
      this.externalLabelText = resolved;
      if (!(this.cd as ViewRef).destroyed) {
        this.cd.detectChanges();
      }
    }
  }

  /** Returns the display label of the currently selected option, or '' if none. */
  private getSelectedLabel(): string {
    const selected: any = this.ngSelect?.selectedItems?.[0];
    if (selected && typeof selected.label === 'string' && selected.label.trim().length > 0) {
      return selected.label;
    }
    const fallback = this.displayText(this.selectedItem);
    return typeof fallback === 'string' ? fallback.trim() : '';
  }

  clear() {
    //debugger;
    this.selectedItem = null;
  }

  showLoadingIcon(sl: boolean) {
    this.showLoading = sl;
    this.cd.detectChanges();

  }

  displayText(item: SelectItem) {
    if (item && item.value && item.label) {
      return item.label;
    } else {
      return item;
    }

  }
}
